/**
 * Structured logger for the application
 * 
 * This logger provides consistent formatting and conditional logging
 * based on environment. It can be extended to send logs to external
 * services in production.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

// Only show warnings and errors in production
const MIN_LOG_LEVEL = process.env.NODE_ENV === 'production' 
  ? LogLevel.WARN
  : LogLevel.DEBUG;

// Enable more detailed auth logs for debugging
const AUTH_DEBUG = process.env.AUTH_DEBUG === 'true';

// For returning structured data that can be serialized
export type LogData = Record<string, any>;

/**
 * Process any error into a loggable format
 */
function formatError(error: unknown): LogData {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack
    };
  }
  
  if (typeof error === 'object' && error !== null) {
    return { ...error };
  }
  
  return { error: String(error) };
}

/**
 * Creates a logger instance for a specific module
 * 
 * @param module The module name to include in log entries
 * @returns Logger instance with debug, info, warn, and error methods
 */
export function createLogger(module: string) {
  const isAuthModule = module.startsWith('auth:');
  
  // Use a lower minimum level for auth modules when AUTH_DEBUG is enabled
  const effectiveMinLevel = (AUTH_DEBUG && isAuthModule) 
    ? LogLevel.DEBUG 
    : MIN_LOG_LEVEL;
  
  // Create a request ID if we're in a request context (improves traceability)
  const requestId = typeof crypto !== 'undefined' 
    ? crypto.randomUUID().substring(0, 8)
    : Math.random().toString(36).substring(2, 10);
    
  return {
    debug: (message: string, data?: unknown) => {
      if (effectiveMinLevel <= LogLevel.DEBUG) {
        if (data !== undefined) {
          console.debug(`[${module}:${requestId}] ${message}`, data);
        } else {
          console.debug(`[${module}:${requestId}] ${message}`);
        }
      }
    },
    
    info: (message: string, data?: unknown) => {
      if (effectiveMinLevel <= LogLevel.INFO) {
        if (data !== undefined) {
          console.info(`[${module}:${requestId}] ${message}`, data);
        } else {
          console.info(`[${module}:${requestId}] ${message}`);
        }
      }
    },
    
    warn: (message: string, data?: unknown) => {
      if (effectiveMinLevel <= LogLevel.WARN) {
        if (data !== undefined) {
          console.warn(`[${module}:${requestId}] ${message}`, data);
        } else {
          console.warn(`[${module}:${requestId}] ${message}`);
        }
      }
    },
    
    error: (message: string, error?: unknown) => {
      if (effectiveMinLevel <= LogLevel.ERROR) {
        if (error !== undefined) {
          console.error(`[${module}:${requestId}] ${message}`, formatError(error));
        } else {
          console.error(`[${module}:${requestId}] ${message}`);
        }
      }
    },
    
    // Create a child logger with additional context (useful for req/res contexts)
    child: (context: Record<string, any>) => {
      return createLogger(`${module}:${Object.values(context).join(':')}`);
    }
  };
}